<template>
  <layout title="Job Applicant Privacy Policy">
    <textFirstSection title="Job Applicant Privacy Policy" />
    <div class="flex justify-center p-6">
      <div class="mt-6 html-editor max-w-6xl mb-6">
        <h5>LAST UPDATED: 21 NOVEMBER 2024</h5>
        <p>
          At Youverse, we value your privacy and are dedicated to safeguarding
          the personal information of job applicants. This policy explains how
          we collect, use, and protect your data during the hiring process. It
          also outlines your rights and our responsibilities under GDPR.
        </p>
        <h2>Definitions</h2>
        <ul>
          <li>
            <strong>Job Applicant Privacy Policy:</strong> This document may
            also be referred to as “Notice“ or “Policy”.
          </li>
          <li>
            <strong>Youverse:</strong> The company referred to as “we,” “us,” or
            “our,” officially known as YOONIK – AUTHENTICATION AND PRIVACY
            SYSTEMS, S.A.
          </li>
          <li><strong>You:</strong> Refers to the job applicants.</li>
        </ul>
        <h2>Information We Collect</h2>
        <p>
          We collect personal information from job applicants to comply with
          legal, regulatory, and internal policies, and to effectively manage
          our business operations. The types of personal information we may
          collect include:
        </p>

        <ol>
          <li>Full name</li>

          <li>Contact details (email, phone number, address)</li>
          <li>Resume/CV, cover letter, and job title</li>
          <li>
            Protected characteristics (e.g., gender, race, medical conditions)
            if you choose to provide them
          </li>
          <li>Employment history</li>
          <li>Educational background</li>
          <li>References</li>
          <li>
            Information from internal communications related to your application
            (e.g., interview scheduling)
          </li>
          <li>
            Any other information you submit during the application process
          </li>
        </ol>
        <br />
        <p>We may also gather information from:</p>

        <ol>
          <li>Your direct submissions</li>
          <li>
            Public sources (e.g., social media profiles, personal websites)
          </li>
          <li>
            Third parties (e.g., professional references, background check
            services, previous employers)
          </li>
        </ol>

        <h2>How We Use Your Information</h2>

        <p>Your information may be used for various purposes, including:</p>
        <ol>
          <li>Assessing your qualifications for the position</li>
          <li>
            Communicating about your application status and future opportunities
          </li>
          <li>Conducting background and reference checks</li>
          <li>Maintaining job application records</li>
          <li>Monitoring equal opportunity and diversity compliance</li>
          <li>Meeting legal and regulatory obligations</li>
          <li>Supporting legal claims or allegations</li>
          <li>Enhancing security and preventing fraud or misconduct</li>
        </ol>
        <h2>Disclosure of Information</h2>

        <p>
          We may share your personal information with service providers for
          legitimate business purposes, such as:
        </p>
        <ol>
          <li>Extending job offers and providing benefits</li>
          <li>Complying with internal policies and legal obligations</li>
          <li>Maintaining job application records</li>
          <li>Improving our business operations</li>
          <li>Enhancing security measures to protect our systems</li>
        </ol>
        <br />
        <p>
          Service providers must adhere to our confidentiality and security
          requirements when handling your information. We may disclose your
          information to:
        </p>
        <ol>
          <li>HR, benefits, and payroll systems</li>
          <li>IT and software providers</li>
          <li>Recruitment tools</li>
        </ol>
        <br />
        <p>Additionally, we may share your information with:</p>
        <ol>
          <li>Third parties you have consented to</li>
          <li>Third parties as required by law</li>
        </ol>
        <br />
        <p>
          Youverse does not share personal information for cross-context
          behavioral advertising.
        </p>

        <h2>Sensitive Personal Information</h2>

        <p>
          We limit the use and disclosure of sensitive personal information to
          comply with legal obligations, manage our business, and respond to
          applicant requests. This may include:
        </p>
        <ol>
          <li>Log-in credentials</li>
          <li>
            Government-issued identification (e.g., passport, driver’s license)
          </li>
          <li>Information for background checks</li>
          <li>
            Health or financial information related to payroll and benefits
          </li>
          <li>Diversity or demographic data</li>
        </ol>

        <h2>Data Retention</h2>

        <p>
          We will retain your personal information for as long as necessary to
          fulfill the purposes outlined in this policy or as required by law. If
          you are not selected for the position, we may retain your information
          for future job opportunities for up to 5 years.
        </p>
        <p>
          By applying to a job offer on our website, you authorize and consent
          to the retention of your data for future job openings for the period
          mentioned above.
        </p>
        <p>
          Please note that applications submitted outside of our website (e.g.,
          via LinkedIn) are subject to the terms and conditions of those
          platforms.
        </p>

        <h2>Data Security</h2>

        <p>
          We implement appropriate security measures to protect your personal
          information from unauthorized access, use, or disclosure. This
          includes physical, electronic, and managerial safeguards.
        </p>

        <h2>Your Rights</h2>

        <p>You have the right to:</p>

        <ol>
          <li>Access your personal information</li>
          <li>Request corrections to your personal data</li>
          <li>Request deletion of your personal information</li>
          <li>Withdraw your application at any time</li>
        </ol>

        <h2>Changes to This Policy</h2>

        <p>
          We may update this privacy policy periodically. Any changes will be
          posted on our website, and we encourage you to review this policy
          regularly.<br />
        </p>
        <h2>Contact Us</h2>

        <p>
          To exercise your rights or if you have any questions or concerns
          regarding this Job Applicant Privacy Policy, please reach out to us at
          <a href="mailto:dpo@youverse.id">dpo@youverse.id</a>.
        </p>
      </div>
    </div>
  </layout>
</template>

<script>
const layout = () =>
  import(/* webpackChunkName: "layout" */ "../Components/Layout/layout.vue");
const textFirstSection = () =>
  import(
    /* webpackChunkName: "textFirstSection" */ "../Components/Layout/textFirstSection.vue"
  );
export default {
  components: { layout, textFirstSection },
};
</script>

<style>
@import "../../css/custom.css";
</style>